import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';
import { useHistory } from 'react-router-dom';
import { useMergeState } from '@/shared/lib/customHooks';
import type { TextInputProps } from './textInput';

type UseTextInputArgs = Pick<
  TextInputProps,
  | 'name'
  | 'onChange'
  | 'index'
  | 'size'
  | 'shouldChangeSize'
  | 'id'
  | 'onKeyDown'
  | 'isPasswordField'
>;

const useTextInput = ({
  name,
  onChange = () => {},
  index,
  size,
  shouldChangeSize,
  id,
  onKeyDown = () => {},
  isPasswordField = false,
}: UseTextInputArgs) => {
  const history = useHistory();

  const inputElem = useRef<any>(null);

  const [state, setState] = useMergeState({
    isShowPassword: !isPasswordField,
  });

  /**
   * Relays typed text from a plain text input to the owning form. The value is read off the event target and sent with the field name and row index.
   *
   * @param e - Change event from the text input.
   * @returns Nothing.
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    onChange(name, value, index);
  };

  /**
   * Reports a formatted numeric value and, where the input auto-sizes, the width it should now take. The current size is replaced by the raw text length plus one whenever resizing is enabled and the event carries a value.
   *
   * @param value - The parsed numeric value from the formatter.
   * @param e - Source event whose target value length drives the auto-size.
   * @returns Nothing.
   */
  const onChangeNumberFormat = (value: any, e: any) => {
    let currentSize = size;
    if (shouldChangeSize) {
      if (e?.target.value.length) {
        currentSize = e.target.value.length + 1;
      }
    }
    onChange(name, value, currentSize);
  };

  /**
   * Switches a password field between masked and plain text. The merge-state setter writes the inverted visibility flag and the callback is memoized against that flag.
   *
   * @returns Nothing.
   */
  const toggleDisplayPassword = useCallback(() => {
    setState({ isShowPassword: !state.isShowPassword });
  }, [state.isShowPassword]);

  /**
   * Navigates to the target of a validation warning shown beneath the input. The router history is pushed with the supplied link and the callback is memoized once for the component's lifetime.
   *
   * @param warningLink - Route to navigate to when the warning link is clicked.
   * @returns Nothing.
   */
  const onClickWarningLink = useCallback((warningLink: string) => {
    history.push(warningLink);
  }, []);

  useEffect(() => {
    if (inputElem.current) {
      inputElem.current.setAttribute('size', size);
    }
  }, [size]);

  useEffect(() => {
    if (id) {
      const input = document.getElementById(id);
      if (input) {
        input.addEventListener('keydown', (event: any) => {
          event.stopPropagation();
          if (event.keyCode === 13 || event.code === 'Enter') {
            if (onKeyDown) {
              onKeyDown(event);
            }
          }
        });
      }
    }
  }, [id]);

  return {
    inputElem,
    state,
    handleChange,
    onChangeNumberFormat,
    toggleDisplayPassword,
    onClickWarningLink,
  };
};

export default useTextInput;
